import { useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { FaSave } from "react-icons/fa";
import api from '../../api/manage_database/LocationReq';
import { toast } from 'sonner';
import BackButton from "../../components/BackButton";
import SelectLocationTypeLine from "../../components/SelectLocationTypeLine";
import InputPlace from "../../components/InputPlace";
import InputThoroughfare from "../../components/InputThoroughfare";


const LocationNew = ({ embedded = false, onCreated, onSavingChange }) => {

    const [place, setPlace] = useState('');
    const [thoroughfare, setThoroughfare] = useState('');
    const [locationTypeLines, setLocationTypeLines] = useState([]);
    const [selectedLocationTypeLine, setSelectedLocationTypeLine] = useState('');
    const [existingLocations, setExistingLocations] = useState([]);
    const [selectedExistingLocation, setSelectedExistingLocation] = useState('');
    const [loadingLocations, setLoadingLocations] = useState(false);
    const [locationsError, setLocationsError] = useState('');
    const [reloadLocations, setReloadLocations] = useState(0);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const savingRef = useRef(false);
    
    const navigate = useNavigate();

    useEffect(() => {
        const fetchLocationTypes = async () => {
            try {
                const response = await api.get('type-lines', {withCredentials: true});
                if (response.data.success) {
                    setLocationTypeLines(response.data.payload.type_lines);
                } else {
                    toast.error('Not got data from database');
                }
            } catch (err) {
                console.log(err.response);
            }
        };

        fetchLocationTypes();
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        const fetchExistingLocations = async () => {
            setLoadingLocations(true);
            setLocationsError('');
            try {
                const response = await api.get('all', { withCredentials: true, signal: controller.signal });
                if (controller.signal.aborted) return;
                if (response.data.success || response.data.message === '[ERROR] No Locations found in database') {
                    setExistingLocations(response.data.payload || []);
                } else {
                    setLocationsError('Could not load existing locations. You can retry or enter a new location.');
                }
            } catch (error) {
                if (!controller.signal.aborted) {
                    setLocationsError('Could not load existing locations. You can retry or enter a new location.');
                }
            } finally {
                if (!controller.signal.aborted) setLoadingLocations(false);
            }
        };
        fetchExistingLocations();
        return () => controller.abort();
    }, [reloadLocations]);

    const normalizeName = (value) => value.trim().toLowerCase();
    const duplicateLocation = selectedLocationTypeLine !== '' && existingLocations.some((location) => (
        normalizeName(location.place) === normalizeName(place)
        && normalizeName(location.thoroughfare || '') === normalizeName(thoroughfare)
        && location.type_line === selectedLocationTypeLine
    ));
    const duplicateMessage = 'This Place / Section / Type Line combination already exists. Choose another direction or change Place / Section.';

    const selectedSourceLocation = existingLocations.find((location) => location.id === selectedExistingLocation);
    const existingPlaces = [...new Set(existingLocations.map((location) => location.place))];
    const locationsForPlace = selectedSourceLocation
        ? existingLocations.filter((location) => location.place === selectedSourceLocation.place)
        : [];
    const existingSections = [...new Set(locationsForPlace.map((location) => location.thoroughfare || ''))];
    const locationsForSection = selectedSourceLocation
        ? locationsForPlace.filter((location) => (location.thoroughfare || '') === (selectedSourceLocation.thoroughfare || ''))
        : [];
    const existingDirections = [...new Set(locationsForSection.map((location) => location.type_line))];

    const fillFromExistingLocation = (location) => {
        setSaveError('');
        setSelectedExistingLocation(location?.id || '');
        if (location) {
            setPlace(location.place);
            setThoroughfare(location.thoroughfare || '');
            setSelectedLocationTypeLine(location.type_line);
        }
    };

    const handleInputPlace = (e) => {
        setSaveError('');
        setPlace(e.target.value);
        setSelectedExistingLocation('');
    }

    const handleInputThoroughfare = (e) => {
        setSaveError('');
        setThoroughfare(e.target.value);
        setSelectedExistingLocation('');
    }

    const handleAdd = async () => {
        if (savingRef.current) return;
        setSaveError('');

        if (duplicateLocation) {
            toast.error(duplicateMessage);
            return;
        }

        if (selectedLocationTypeLine === '') {
            toast.error('Set correct direction! (Type)')
            return
        }

        if (place.trim() === '') {
            toast.error('Set place! Place field cannot be empty!')
            return
        }

        if (place === '') {
            toast.warning('Thoroughfare field is empty but possible to set!')
        }

        savingRef.current = true;
        setSaving(true);
        onSavingChange?.(true);
        try {
            const response = await api.post(
                '',
                {
                    place: place.trim(),
                    thoroughfare: thoroughfare.trim(),
                    type_line: selectedLocationTypeLine
                },
                {
                    withCredentials: true
                }
            );
            if (response.data.success) {
                toast.success(response.data.message);
                if (embedded) {
                    onCreated?.(response.data.payload[0]);
                    return;
                }
                navigate(
                    `../record/${response.data.payload[0].id}`,
                    {
                        state: {
                            addRecordMessage: 'Added record to database!',
                            anotherTypeOfPage: true
                        }
                    }
                )
            } else {
                toast.error(response.data.message);
                setSaveError(response.data.message || 'Could not add the location. Please try again.');
            }
        } catch (err) {
            const message = err.response?.data?.message || 'Could not add the location. Please try again.';
            setSaveError(message);
            toast.error(message);
        } finally {
            savingRef.current = false;
            setSaving(false);
            onSavingChange?.(false);
        }
    };

    return (
        <div className={`location-new-container${embedded ? ' location-new-inline' : ''}`}>
            {embedded ? <h3 className='location-new-title'>Add Location</h3> : <h2 className='location-new-title'>Location</h2>}
            <div className='location-new-card'>
                <div className="location-new-label-select-wrapper">
                    <label
                        htmlFor='place-input'
                        className='bold-label'
                    >
                        Place:
                    </label>
                    <InputPlace 
                        place={place}
                        handleInputPlace={handleInputPlace}
                    />
                </div>
                <div className="location-new-label-select-wrapper">
                    <label
                        htmlFor='thoroughfare-input'
                        className='bold-label'
                    >
                        Section:
                    </label>
                    <InputThoroughfare
                        thoroughfare={thoroughfare}
                        handleInputThoroughfare={handleInputThoroughfare}
                    />
                </div>
                <div className="location-new-label-select-wrapper">
                    <label
                        htmlFor='location-type-line-select'
                        className='bold-label'
                    >
                        Type Line:
                    </label>
                    <SelectLocationTypeLine
                        LocationTypeLines={locationTypeLines}
                        selectedlocationTypeLine={selectedLocationTypeLine}
                        setSelectedLocationTypeLine={(value) => {
                            setSaveError('');
                            setSelectedLocationTypeLine(value);
                            setSelectedExistingLocation('');
                        }}
                    />
                </div>
                {(duplicateLocation || saveError) && (
                    <p className="location-new-inline-error" role="alert">{duplicateLocation ? duplicateMessage : saveError}</p>
                )}
                {embedded && (
                    <section className="location-new-existing" aria-label="Existing locations">
                        <p className="bold-label">Locations in database:</p>
                        <p>Select a place, section and type line to fill in the fields above. You can edit the details before adding.</p>
                        {loadingLocations ? <p role="status">Loading locations...</p> : locationsError ? (
                            <div role="alert">
                                <p>{locationsError}</p>
                                <button type="button" className="location-new-existing-retry" onClick={() => setReloadLocations((previous) => previous + 1)}>Retry</button>
                            </div>
                        ) : existingLocations.length === 0 ? <p>No locations in the database yet.</p> : (
                            <div className="location-new-existing-choices">
                                <div className="location-new-existing-choice">
                                    <label htmlFor="existing-place-select" className="bold-label">Select a place:</label>
                                    <select
                                        id="existing-place-select"
                                        className="choice-select location-new-existing-select"
                                        value={selectedSourceLocation?.place || ''}
                                        onChange={(event) => fillFromExistingLocation(existingLocations.find((location) => location.place === event.target.value))}
                                        disabled={saving}
                                    >
                                        <option value="">Select a place...</option>
                                        {existingPlaces.map((name) => <option key={name} value={name}>{name}</option>)}
                                    </select>
                                </div>
                                <div className="location-new-existing-choice">
                                    <label htmlFor="existing-section-select" className="bold-label">Select a section:</label>
                                    <select
                                        id="existing-section-select"
                                        className="choice-select location-new-existing-select"
                                        value={selectedSourceLocation?.thoroughfare || ''}
                                        onChange={(event) => fillFromExistingLocation(locationsForPlace.find((location) => (location.thoroughfare || '') === event.target.value))}
                                        disabled={saving || !selectedSourceLocation}
                                    >
                                        {!selectedSourceLocation && <option value="">Select a section...</option>}
                                        {existingSections.map((name) => <option key={name} value={name}>{name || 'No section'}</option>)}
                                    </select>
                                </div>
                                <div className="location-new-existing-choice">
                                    <label htmlFor="existing-type-line-select" className="bold-label">Select a type line:</label>
                                    <select
                                        id="existing-type-line-select"
                                        className="choice-select location-new-existing-select"
                                        value={selectedSourceLocation?.type_line || ''}
                                        onChange={(event) => fillFromExistingLocation(locationsForSection.find((location) => location.type_line === event.target.value))}
                                        disabled={saving || !selectedSourceLocation}
                                    >
                                        {!selectedSourceLocation && <option value="">Select a type line...</option>}
                                        {existingDirections.map((direction) => <option key={direction} value={direction}>{direction}</option>)}
                                    </select>
                                </div>
                            </div>
                        )}
                    </section>
                )}
                <button type="button" className='location-new-button' onClick={handleAdd} disabled={saving || duplicateLocation}>
                    {saving ? 'Saving...' : embedded ? 'Add location' : 'Add'} &nbsp; <FaSave/>
                </button>
            </div>
            {!embedded && <BackButton navigateTo='/manage-database'/>}
        </div>
    );
        
};

export default LocationNew;
